/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 1: which sidebar items each person sees, where they go, and which
 * one is active. Every item opens a page that exists today; the rebuilt pages
 * replace the targets one step at a time (features/F032-dashboard-redesign).
 */

import { todayRole } from "../lib/today";

export type ShellItemKey =
  | "today"
  | "setup"
  | "schedule"
  | "members"
  | "requests"
  | "memberships"
  | "programming"
  | "team"
  | "training"
  | "library"
  | "book"
  | "membership"
  | "admin"
  | "coaching"
  | "settings";

export interface ShellAccess {
  hasBoxRights: boolean;
  hasCoachRights: boolean;
  hasAdminRights: boolean;
  isBoxAdmin: boolean;
  isBoxCoach: boolean;
  isBoxStaff: boolean;
  /** Holds the coach claim outside a pure box-coach role: owns a coaching business (Coach portal). */
  coachesIndependently: boolean;
  canSetUpGym: boolean;
  canManageClasses: boolean;
  bookingEnabled: boolean;
  coachingEnabled: boolean;
  hasBox: boolean;
  isActiveMember: boolean;
}

export interface ShellItem {
  key: ShellItemKey;
  label: string;
  /** Path under the dashboard segment, with an optional query string. */
  to: string;
  /** Shown but not usable yet: gym pages before the gym exists. */
  locked?: boolean;
}

export interface ShellGroup {
  id: "setup" | "gym" | "coaching" | "training" | "member" | "lemonade";
  /** Null renders the items without a group heading. */
  label: string | null;
  items: ShellItem[];
}

export interface ShellNav {
  groups: ShellGroup[];
  footer: ShellItem[];
}

export function buildShellNav(a: ShellAccess): ShellNav {
  const canSeeClasses = a.hasBox && a.bookingEnabled && (a.hasBoxRights || a.isActiveMember);
  // Mirrors CoachPortalPage and the old sidebar's Coach item.
  const canSeeCoachPortal =
    a.hasCoachRights && (a.hasAdminRights || (a.hasBoxRights ? a.coachingEnabled : true));
  const canSeeRoster = a.hasCoachRights || a.isBoxAdmin;
  const canSeeSchedule =
    a.bookingEnabled && (a.hasBoxRights || (a.isBoxCoach && a.canManageClasses));
  const runsGym = a.hasBoxRights && a.hasBox;

  const groups: ShellGroup[] = [];

  if (a.canSetUpGym) {
    groups.push({
      id: "setup",
      label: null,
      items: [{ key: "setup", label: "Set up your gym", to: "setup" }],
    });
  }
  const work: ShellItem[] = [];
  if (todayRole(a) !== null) {
    work.push({ key: "today", label: "Today", to: "today" });
  }
  if (canSeeSchedule) work.push({ key: "schedule", label: "Schedule", to: "box-portal/booking" });
  if (canSeeRoster) {
    const label = runsGym ? "Members" : a.isBoxCoach ? "My athletes" : "My clients";
    work.push({ key: "members", label, to: "athletes" });
  }
  // The same people the Memberships page lets in: the gym's owner and admins.
  if (runsGym && a.isBoxAdmin) {
    work.push({ key: "memberships", label: "Memberships", to: "gym-memberships" });
  }
  if (a.isBoxAdmin || (a.isBoxCoach && a.hasBox)) {
    work.push({ key: "programming", label: "Programming", to: "programming" });
  } else if (canSeeCoachPortal) {
    work.push({ key: "programming", label: "Programming", to: "coach-portal" });
  }
  if (a.isBoxAdmin) work.push({ key: "team", label: "Team", to: "team" });
  // A gym admin with their own coaching business keeps its landing page and payouts.
  if (a.isBoxAdmin && a.coachesIndependently && canSeeCoachPortal) {
    work.push({ key: "coaching", label: "My coaching", to: "coach-portal" });
  }
  // Before the gym exists its pages are shown locked, so nothing is a surprise.
  // Only for a plain new owner: someone who already coaches keeps their real pages.
  if (a.canSetUpGym && !a.hasBox && work.length === 0) {
    groups.push({
      id: "gym",
      label: "Your gym",
      items: (
        [
          ["today", "Today"],
          ["schedule", "Schedule"],
          ["members", "Members"],
          ["memberships", "Memberships"],
          ["team", "Team"],
        ] as const
      ).map(([key, label]) => ({ key, label, to: "setup", locked: true })),
    });
  }

  if (work.length > 0) {
    groups.push(
      runsGym
        ? { id: "gym", label: "Your gym", items: work }
        : { id: "coaching", label: "Coaching", items: work }
    );
  }

  const staffLike = work.length > 0 || a.canSetUpGym;
  const own: ShellItem[] = [
    { key: "training", label: staffLike ? "My training" : "Home", to: staffLike ? "training" : "" },
  ];
  if (canSeeClasses)
    own.push({ key: "book", label: staffLike ? "Book a class" : "Classes", to: "classes" });
  own.push({ key: "library", label: "Workout library", to: "workout-library" });
  if (!staffLike) {
    own.push({ key: "membership", label: "My membership", to: "profile?tab=memberships" });
  }
  groups.push(
    staffLike
      ? { id: "training", label: "My training", items: own }
      : { id: "member", label: null, items: own }
  );

  if (a.hasAdminRights) {
    groups.push({
      id: "lemonade",
      label: "Lemonade",
      items: [{ key: "admin", label: "Admin portal", to: "admin-portal" }],
    });
  }

  // Gym settings exists only once there is a gym to set up and the right to.
  const footer: ShellItem[] =
    a.hasBox && a.isBoxAdmin
      ? [{ key: "settings", label: "Gym settings", to: "gym-settings" }]
      : [];

  return { groups, footer };
}

/**
 * The item a location belongs to. `rest` is the path after the dashboard
 * segment without a leading slash ("" for the dashboard home), `tab` the
 * Business Portal's ?tab= value. Deeper pages keep their section lit: a class's
 * details light Schedule, a programme lights Programming.
 */
export function activeShellKey(rest: string, tab: string | null): ShellItemKey | null {
  const path = rest.replace(/\/+$/, "");
  if (path === "") return "training";
  if (path === "today") return "today";
  if (path.startsWith("gym-settings")) return "settings";
  if (path === "setup") return "setup";
  if (path === "programming" || path.startsWith("programming/")) return "programming";
  if (path === "team" || path.startsWith("team/")) return "team";
  if (path === "gym-memberships" || path.startsWith("gym-memberships/")) return "memberships";
  if (path === "training") return "training";
  // A movement's page and Records belong to My training (members' Home).
  if (path.startsWith("movements/")) return "training";
  if (path.startsWith("box-portal/booking") || path.startsWith("box-portal/classes")) {
    return "schedule";
  }
  if (path === "box-portal") {
    if (tab === "membership") return "memberships";
    if (tab === "programming") return "programming";
    if (tab === "people" || tab === "staff") return "team";
    // Admin, landing, terms, Stripe, or no tab: all of it becomes Gym settings.
    return "settings";
  }
  if (path.startsWith("coach-portal") || path.startsWith("workout-programme")) return "programming";
  if (path.startsWith("athletes")) return "members";
  if (path.startsWith("requests")) return "members";
  if (path.startsWith("classes")) return "book";
  if (path.startsWith("workout-library")) return "library";
  if (path.startsWith("profile") && tab === "memberships") return "membership";
  if (path.startsWith("membership/")) return "membership";
  if (path.startsWith("admin-portal") || path.startsWith("admin/")) return "admin";
  return null;
}

/**
 * The location's key, settled against the items this person actually has: the
 * Business Portal is "Set up your gym" before a gym exists, and the Coach
 * portal is "My coaching" for an admin who also has Programming elsewhere.
 */
export function resolveActiveKey(nav: ShellNav, key: ShellItemKey | null, rest: string) {
  const has = (k: ShellItemKey) => findShellItem(nav, k) !== null;
  if (key === "settings" && !has("settings") && has("setup")) return "setup";
  if (key === "programming" && rest.startsWith("coach-portal") && has("coaching"))
    return "coaching";
  return key;
}

/** The item and its group for the breadcrumb, or null off the menu (e.g. My profile). */
export function findShellItem(
  nav: ShellNav,
  key: ShellItemKey | null
): { item: ShellItem; group: ShellGroup | null } | null {
  if (!key) return null;
  for (const group of nav.groups) {
    const item = group.items.find((i) => i.key === key);
    if (item) return { item, group };
  }
  const footerItem = nav.footer.find((i) => i.key === key);
  return footerItem ? { item: footerItem, group: null } : null;
}

/**
 * My training's deeper crumbs: "My training › Records" on its Records tab and
 * "My training › Back Squat" on a movement's page (`pageTitle`, published by
 * the page). Null elsewhere, where the menu item names the page.
 */
export function trainingCrumb(
  rest: string,
  tab: string | null,
  pageTitle: string | null
): { parent: string; title: string } | null {
  const path = rest.replace(/\/+$/, "");
  if ((path === "" || path === "training") && tab === "records") {
    return { parent: "My training", title: "Records" };
  }
  if (path.startsWith("movements/")) {
    return { parent: "My training", title: pageTitle || "Movement" };
  }
  return null;
}

/** Page names for places that are not menu items, so the top bar never goes blank. */
export function shellPageTitle(rest: string): string | null {
  const path = rest.replace(/\/+$/, "");
  if (path === "setup") return "Set up your gym";
  if (path === "programming/new") return "New program";
  if (path === "gym-memberships/new") return "New membership";
  if (path.startsWith("profile")) return "My profile";
  if (path.startsWith("movements/")) return "Movement";
  if (path.startsWith("membership/")) return "My membership";
  if (path.startsWith("workout-programme")) return "Program";
  if (path.startsWith("box-portal/classes")) return "Class";
  if (path === "box-portal" || path === "gym-settings") return "Gym settings";
  if (path === "team") return "Team";
  if (path.startsWith("coach-portal")) return "Coaching";
  return null;
}
